import { Tree } from "~/features/freeCapacityPush/components/Tree/Tree";
import { TextWithHighlight } from "~/common/components/TextWithHighlight";
import { Stack, styled } from "~/common/mui";
import { LinkButton } from "~/common/components/Link";
import { useTranslation } from "react-i18next";

const HIGHLIGHT_CLASS_NAME = "fcc-tree-highlight";

export interface FCCTreeNode {
  key: string;
  label: string;
  quantity: number;
  children: FCCTreeNode[];
}

interface Props {
  className?: string;
  items: FCCTreeNode[];
  searchTerm: string;
  selected: string | null;
  setSelected: (selected: FCCTreeNode | null) => void;
  expanded: string[];
  setExpanded: (expanded: string[]) => void;
  onReset?: () => void;
}

const ResetButton = styled(LinkButton)({
  placeSelf: "flex-end",
});

const Node = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "space-between",
  [`& .${HIGHLIGHT_CLASS_NAME}`]: {
    fontWeight: "bold",
  },
});

const Quantity = styled("span")(({ theme }) => ({
  paddingInline: theme.spacing(1),
  alignSelf: "center",
}));

export const FCCTree = ({ expanded, setExpanded, onReset, ...props }: Props) => {
  const { t } = useTranslation();
  const selected = props.selected ? [props.selected] : [];
  const renderItem = (item: FCCTreeNode) => renderNode(item, props.searchTerm);
  return (
    <Stack gap={3}>
      <Tree
        onToggleExpandItem={(key: string, isExpanded: boolean) => {
          if (isExpanded) {
            setExpanded([...expanded, key]);
          } else {
            setExpanded(expanded.filter((k) => k !== key));
          }
        }}
        expanded={expanded}
        selected={selected}
        onSelectItem={(item: FCCTreeNode, isSelected: boolean) => {
          props.setSelected(isSelected ? item : null);
        }}
        items={props.items}
        renderItem={renderItem}
      />
      {onReset && (
        <ResetButton onClick={onReset}>
          {t("page.linear.links.reset")}
        </ResetButton>
      )}
    </Stack>
  );
};

const renderNode = (item: FCCTreeNode, searchTerm: string) => {
  return (
    <Node>
      <TextWithHighlight highlight={searchTerm} highlightClass={HIGHLIGHT_CLASS_NAME}>
        {item.label}
      </TextWithHighlight>
      <Quantity>({item.quantity})</Quantity>
    </Node>
  );
};
